<template>
  <Message v-if="alert" :severity="alert.severity" closable @close="dismiss">
    <div class="flex flex-wrap items-center gap-2">
      <span>{{ alert.text }}</span>
      <RouterLink :to="{ name: 'ManagementLicense' }" class="font-semibold underline">
        {{ t('seeLicense') }}
      </RouterLink>
    </div>
  </Message>
</template>

<script setup lang="ts">
import { hasPermission } from '@/composables/RequiredPermissions'
import { PERMISSIONS } from '@/models/enums/permissions'
import { ILicenseSituation, LICENSE_WARNING_PERCENT } from '@/models/license'
import * as licenseService from '@/services/licenseService'
import { useLanguageStore } from '@/stores/LanguageStore'
import Message from 'primevue/message'
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'

/**
 * Aviso de licença para administradores, em todas as páginas: limite próximo, atingido ou
 * excedido. Consulta uma vez por sessão; fechar vale até o próximo login.
 */
const languageStore = useLanguageStore()
const route = useRoute()
const t = (key: string, params?: Record<string, unknown>) =>
  languageStore.t(`views.license.alert.${key}`, params ?? {})

const DISMISS_KEY = 'licenseAlertDismissed'
const situation = ref<ILicenseSituation | null>(null)
const dismissed = ref(sessionStorage.getItem(DISMISS_KEY) === 'true')

const alert = computed(() => {
  const s = situation.value
  // A propria tela de licenca ja mostra a situacao com detalhe.
  if (!s || dismissed.value || route.name === 'ManagementLicense') return null
  // Cliente sem contrato por pontos: nada a avisar.
  if (!s.ativa) return null
  if (s.excedida)
    return { severity: 'error', text: t('exceeded', { used: s.usados, total: s.totalPontos }) }
  if (s.limiteAtingido)
    return { severity: 'error', text: t('limitReached', { total: s.totalPontos }) }
  if (s.percentualUso >= LICENSE_WARNING_PERCENT)
    return { severity: 'warn', text: t('nearLimit', { percent: s.percentualUso }) }
  return null
})

const dismiss = () => {
  dismissed.value = true
  sessionStorage.setItem(DISMISS_KEY, 'true')
}

onMounted(() => {
  if (dismissed.value || !hasPermission(PERMISSIONS.MANAGE.LICENSE_MANAGE)) return
  licenseService
    .getSituation()
    .then((res) => (situation.value = res.data))
    .catch(() => (situation.value = null))
})
</script>
